import { useQuery } from '@tanstack/react-query';

import { freePassQueries } from '../api/freePassQueries';
import useFreePassEnabled from './useFreePassEnabled';

/**
 * The per-day reward ladder for a logged-out visitor.
 *
 * Reads the same `user-promos/…/items` endpoint the signed-in path uses, which
 * answers anonymously with the campaign catalogue (every day `locked`, no
 * `claimedAt`) — so it is the real ladder, not a copy that can drift.
 *
 * `retry: false` because the one expected failure is environments where the
 * backend has not opened this endpoint up yet (production still 401s as of
 * 2026-09-22); a second attempt cannot help, and the caller falls back to
 * `VITE_FREE_PASS_GUEST_LADDER`. Never refetched: the response is config.
 *
 * Only the amounts are used. Statuses are deliberately ignored —
 * `buildGuestStreakState` computes the guest's day locally so "a guest never
 * advances" stays a local invariant rather than a promise about what the server
 * will keep returning to anonymous callers.
 */
const useGuestLadderQuery = (promoId: string | undefined) => {
  const enabled = useFreePassEnabled();

  return useQuery({
    ...freePassQueries.guestItems(promoId ?? ''),
    enabled: enabled && Boolean(promoId),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export default useGuestLadderQuery;
